import {
  EstimateAllocationConfig,
  EstimateAllocationResult,
  ValidationResult,
} from "./types";
import { VirtualOrderBlockStatus } from "../../assignSupplierUtils";
import { OrderItem } from "../../orderUtils";
import services from "../../services";
import {
  NormalizedDeliverySelection,
  EnrichedSupplierForDelivery,
  SupplierPartForDelivery,
} from "../../../../components/DeliveryOptions/types";
import {
  OrderItemActionType,
  CUSTOMER_ACTION_TO_API_ACTION,
} from "../../types/orderItemsActionTypes";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { extractErrorMessage } from "../../utils/orderItemsActionUtils";

import { SplitConfirmHandler } from "./handlers/SplitConfirmHandler";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST_TIMEZONE = "Asia/Kolkata";

const BLOCK_REASONS = {
  CUSTOMER_HOLD:
    "Order is currently on customer hold. Please resume the order before assigning suppliers.",
  DELIVERED: "Cannot assign supplier to delivered order.",
  CANCELLED: "Cannot assign supplier to cancelled order.",
  FROZEN: "Order is frozen and cannot be modified.",
} as const;

export class EstimateAllocatorService {
  static async allocate(
    allocationRequest: EstimateAllocationConfig,
    isCustomerOrderFreezed: boolean,
  ): Promise<EstimateAllocationResult> {
    // =========================================================================
    // STEP 1: Validation
    // =========================================================================
    const allocationRequestValidation =
      EstimateAllocatorService.validateAllocationRequest(allocationRequest);
    if (!allocationRequestValidation.isValid) {
      return {
        success: false,
        message:
          allocationRequestValidation.message || "Invalid allocation request",
      };
    }

    const blockStatus = EstimateAllocatorService.validateOrderBlockStatus(
      allocationRequest.sourceOrder,
    );
    if (blockStatus.isBlocked) {
      return {
        success: false,
        message: blockStatus.reason || "Order is blocked",
      };
    }

    const orderData = allocationRequest.sourceOrder;
    const splitSelections = allocationRequest.splitSelections!;
    const splitData = allocationRequest.splitData!;
    const cptGivenTime = allocationRequest.cptGivenTime!;
    const orderNo = orderData.orderNo;
    const estimateNo = allocationRequest.estimateNo || orderNo;

    console.log("[EstimateAllocatorService] Starting allocation", {
      orderNo,
      estimateNo,
      splitSelectionsCount: splitSelections.length,
    });

    // =========================================================================
    // STEP 2: Initialize working state with structuredClone for immutability
    // =========================================================================
    let workingOrderItems: OrderItem[] = structuredClone(
      allocationRequest.orderItems,
    );
    let workingOrderEta: Array<{
      etaGivenTime: string;
      eta: string;
      reason?: string;
    }> = structuredClone(allocationRequest.orderEta);
    let isEtaUpdated = false;

    // =========================================================================
    // STEP 3: Process delivery options actions via new API
    // Order: Hold → Cancel → Buy Time
    // =========================================================================
    const holdSelections = splitSelections.filter(
      (s) => s.customerAction === "hold",
    );
    if (holdSelections.length > 0) {
      const holdResult =
        await EstimateAllocatorService.processActionOnOrderItems(
          orderNo,
          CUSTOMER_ACTION_TO_API_ACTION.hold,
          holdSelections,
          splitData,
        );
      if (!holdResult.success) {
        return {
          success: false,
          message: `Failed to hold items: ${holdResult.error}`,
        };
      }

      workingOrderItems = EstimateAllocatorService.applyHoldToItems(
        workingOrderItems,
        holdSelections,
        splitData,
      );
    }

    const cancelSelections = splitSelections.filter(
      (s) => s.customerAction === "cancel",
    );
    if (cancelSelections.length > 0) {
      const cancelResult =
        await EstimateAllocatorService.processActionOnOrderItems(
          orderNo,
          CUSTOMER_ACTION_TO_API_ACTION.cancel,
          cancelSelections,
          splitData,
        );
      if (!cancelResult.success) {
        return {
          success: false,
          message: `Failed to cancel items: ${cancelResult.error}`,
        };
      }

      // Keep local state computation as safeguard
      workingOrderItems = EstimateAllocatorService.applyCancelToItems(
        workingOrderItems,
        cancelSelections,
        splitData,
      );
    }

    const buyTimeSelections = splitSelections.filter(
      (s) => s.customerAction === "buytime",
    );
    if (buyTimeSelections.length > 0) {
      const buyTimeResult = EstimateAllocatorService.computeBuyTimeEta(
        workingOrderEta,
        buyTimeSelections,
        cptGivenTime,
      );
      if (buyTimeResult.isEtaUpdated) {
        workingOrderEta = buyTimeResult.updatedEta;
        isEtaUpdated = true;
      }
    }

    const confirmSelections = splitSelections.filter(
      (s) => s.customerAction === "confirm",
    );

    // =========================================================================
    // STEP 4: Update source order with ETA (only if changed)
    // Moved here to ensure Buy Time ETA is saved even if no confirms exist.
    // =========================================================================
    if (isEtaUpdated) {
      try {
        await EstimateAllocatorService.updateSourceOrder(orderData._id, {
          orderEta: workingOrderEta,
        });
      } catch (error: any) {
        return {
          success: false,
          message: `Failed to update source order: ${error.message || error}`,
          error,
        };
      }
    }

    if (confirmSelections.length === 0 || isCustomerOrderFreezed) {
      if (isCustomerOrderFreezed && confirmSelections.length > 0) {
        return {
          success: false,
          message:
            "Customer is Blocked. Please unblock the customer before confirming items.",
        };
      }

      return {
        success: true,
        message: "Successfully processed actions for the estimate.",
      };
    }

    // =========================================================================
    // STEP 5: Confirm splits via backend API using SplitConfirmHandler
    // The handler creates manual delivery options if needed, then confirms each split.
    // This replaces the legacy frontend orchestration of child order creation.
    // =========================================================================
    const createdOrders: any[] = [];
    let remainingOrder: any;

    try {
      const confirmHandler = new SplitConfirmHandler();
      const results = await confirmHandler.execute(
        estimateNo,
        confirmSelections,
        splitData,
      );

      for (const result of results) {
        if (!result.success || !result.order) continue;

        if (result.supplierId === null || result.supplierId === undefined) {
          remainingOrder = {
            orderId: result.order._id,
            orderNo: result.order.orderNo,
            isAllocated: false,
            orderItems: result.order.orderItems,
          };
        } else {
          const supplier = allocationRequest.suppliers.find(
            (s) => s.supplierId === result.supplierId,
          );
          createdOrders.push({
            orderId: result.order._id,
            orderNo: result.order.orderNo,
            supplierId: result.supplierId,
            supplierName: supplier?.supplierName || "Unknown",
            isAllocated: true,
            orderItems: result.order.orderItems,
          });
        }
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.message || "Failed to confirm selections",
        error,
      };
    }

    // =========================================================================
    // STEP 6: Apply confirm actions (local state only)
    // Backend automatically reduces remainingDecisionQuantity when child orders are created
    // =========================================================================
    workingOrderItems = EstimateAllocatorService.applyConfirmToItems(
      workingOrderItems,
      confirmSelections,
      splitData,
    );

    return {
      success: true,
      message: `Successfully created ${
        createdOrders.length
      } allocated order(s)${remainingOrder ? " and 1 remaining order" : ""}`,
      createdOrders,
      remainingOrder,
      virtualOrderId: allocationRequest.sourceOrder._id,
    };
  }

  /**
   * Validates if the order is blocked from supplier allocation.
   * Checks for CUSTOMER_HOLD, DELIVERED, CANCELLED, FROZEN statuses.
   */
  static validateOrderBlockStatus(orderData: any): VirtualOrderBlockStatus {
    if (orderData.orderStatus === "CUSTOMER_HOLD") {
      return {
        isBlocked: true,
        reason: BLOCK_REASONS.CUSTOMER_HOLD,
        blockType: "CUSTOMER",
      };
    }

    if (orderData.orderStatus === "DELIVERED") {
      return {
        isBlocked: true,
        reason: BLOCK_REASONS.DELIVERED,
        blockType: "SYSTEM",
      };
    }

    if (orderData.orderStatus === "CANCELLED") {
      return {
        isBlocked: true,
        reason: BLOCK_REASONS.CANCELLED,
        blockType: "SYSTEM",
      };
    }

    if (orderData.isCustomerOrderFreezed) {
      return {
        isBlocked: true,
        reason: BLOCK_REASONS.FROZEN,
        blockType: "SYSTEM",
      };
    }

    return { isBlocked: false };
  }

  /**
   * Validates the allocation request.
   * Checks source order data, orderEta, supplier requirements, and delivery options data.
   */
  static validateAllocationRequest(
    allocationRequest: EstimateAllocationConfig,
  ): ValidationResult {
    if (
      !allocationRequest.orderEta ||
      allocationRequest.orderEta.length === 0
    ) {
      return {
        isValid: false,
        message: "Order ETA is required for estimate allocation",
      };
    }

    if (
      !allocationRequest.suppliers ||
      allocationRequest.suppliers.length === 0
    ) {
      return {
        isValid: false,
        message: "At least one supplier is required",
      };
    }

    if (
      !allocationRequest.splitSelections ||
      allocationRequest.splitSelections.length === 0
    ) {
      return {
        isValid: false,
        message: "Split selection data are required for estimate allocation",
      };
    }

    if (
      !allocationRequest.splitData ||
      allocationRequest.splitData.length === 0
    ) {
      return {
        isValid: false,
        message: "Split data is required for estimate allocation",
      };
    }

    if (!allocationRequest.cptGivenTime) {
      return {
        isValid: false,
        message: "CPT given time is required for estimate allocation",
      };
    }

    if (!allocationRequest.estimateNo) {
      return {
        isValid: false,
        message: "Estimate number is required for estimate allocation",
      };
    }

    return { isValid: true };
  }

  /**
   * Updates the source order with orderEta.
   * Backend automatically converts to VIRTUAL and updates descendantOrderIds when child orders are created.
   */
  static async updateSourceOrder(
    sourceOrderId: string,
    payload: {
      orderEta?: Array<{ etaGivenTime: string; eta: string; reason?: string }>;
    },
  ): Promise<any> {
    const updatePayload: any = {
      orderETA: payload.orderEta,
    };

    const response = await services.updateOrder(sourceOrderId, updatePayload);

    return response;
  }

  /**
   * @param selections - Filtered selections for a specific action
   * @param splitData - Split data containing parts with partId
   * @returns Array of items with partId and quantity for API call
   */
  static buildActionItemsPayloadFromSelections(
    selections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): Array<{ partId: string; quantity: number }> {
    const selectionMap = new Map(
      selections.map((s) => [s.supplierId ?? "remainingItems", s]),
    );

    const quantityByPartId = new Map<string, number>();

    splitData.forEach((split) => {
      const selection = selectionMap.get(split.supplierId ?? "remainingItems");
      if (!selection) return;

      split.parts.forEach((part) => {
        const partId = part.partId;
        if (!partId) return;

        const current = quantityByPartId.get(partId) || 0;
        quantityByPartId.set(partId, current + part.quantity);
      });
    });

    const items: Array<{ partId: string; quantity: number }> = [];
    quantityByPartId.forEach((quantity, partId) => {
      if (quantity > 0) {
        items.push({ partId, quantity });
      }
    });

    return items;
  }

  /**
   * @param orderNo - Order number
   * @param action - Action type (HOLD, CANCEL, UNHOLD, CONFIRM)
   * @param items - Array of items with partId and quantity
   * @returns Promise with success status and optional error message
   */
  static async executeOrderItemsAction(
    orderNo: string,
    action: OrderItemActionType,
    items: Array<{ partId: string; quantity: number }>,
  ): Promise<{ success: boolean; error?: string }> {
    if (items.length === 0) {
      return { success: true };
    }

    try {
      await services.orderItemsAction(orderNo, action, items);
      return { success: true };
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Generic method to process delivery option selections via the new API.
   * Builds items payload from selections and executes the specified action.
   * Pure functions (applyHoldToItems, applyCancelToItems, applyConfirmToItems)
   * are still used for local state computation.
   *
   * @param orderNo - Order number
   * @param action - Action type (HOLD, CANCEL, CONFIRM)
   * @param selections - Selections from delivery options modal
   * @param splitData - Split data with parts mapping
   * @returns Promise with success status and optional error message
   */
  static async processActionOnOrderItems(
    orderNo: string,
    action: OrderItemActionType,
    selections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): Promise<{ success: boolean; error?: string }> {
    if (selections.length === 0) {
      return { success: true };
    }

    const items =
      EstimateAllocatorService.buildActionItemsPayloadFromSelections(
        selections,
        splitData,
      );

    return EstimateAllocatorService.executeOrderItemsAction(
      orderNo,
      action,
      items,
    );
  }

  // ============================================================================
  // PURE FUNCTIONS (No API calls - for testability and clarity)
  // ============================================================================

  /**
   * Pure function: Applies hold quantities to order items.
   * Updates holdQuantity and reduces remainingDecisionQuantity.
   * Returns items unchanged if holdSelections is empty/undefined.
   *
   * @param orderItems - Current order items (will be cloned, not mutated)
   * @param holdSelections - Hold selections from delivery options modal
   * @param splitData - Split data with parts mapping
   * @returns New array of order items with hold applied
   */
  static applyHoldToItems(
    orderItems: OrderItem[],
    holdSelections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): OrderItem[] {
    if (!holdSelections || holdSelections.length === 0) {
      return orderItems;
    }

    // Map selections to parts by supplierId (null is valid for remaining items)
    const selectionMap = new Map(
      holdSelections.map((s) => [s.supplierId ?? "remainingItems", s]),
    );

    // Build hold quantity updates map: partNumber -> quantity to hold
    const holdUpdates = new Map<string, number>();

    splitData.forEach((split) => {
      const selection = selectionMap.get(split.supplierId ?? "remainingItems");
      if (!selection) return;

      split.parts.forEach((part) => {
        const key = part.partNumber;
        const currentHold = holdUpdates.get(key) || 0;
        holdUpdates.set(key, currentHold + part.quantity);
      });
    });

    // Update order items
    return orderItems.map((item) => {
      const itemPartNumber = item.partNumber;
      const holdQty = holdUpdates.get(itemPartNumber) || 0;

      if (holdQty === 0) return item;

      const currentHold = item.holdQuantity || 0;
      const currentRemaining = item.remainingDecisionQuantity || 0;

      // Validate: can't hold more than remaining
      const finalHoldQty = Math.min(holdQty, currentRemaining);

      return {
        ...item,
        holdQuantity: currentHold + finalHoldQty,
        remainingDecisionQuantity: currentRemaining - finalHoldQty,
      };
    });
  }

  /**
   * Pure function: Applies cancel quantities to order items.
   * Updates cancelledQuantity and reduces remainingDecisionQuantity.
   * Returns items unchanged if cancelSelections is empty/undefined.
   *
   * @param orderItems - Current order items (will be cloned, not mutated)
   * @param cancelSelections - Cancel selections from delivery options modal
   * @param splitData - Split data with parts mapping
   * @returns New array of order items with cancel applied
   */
  static applyCancelToItems(
    orderItems: OrderItem[],
    cancelSelections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): OrderItem[] {
    if (!cancelSelections || cancelSelections.length === 0) {
      return orderItems;
    }

    // Map selections to parts by supplierId (null is valid for remaining items)
    const cancelSelectionMap = new Map(
      cancelSelections.map((s) => [s.supplierId ?? "remainingItems", s]),
    );

    // Build cancel quantity updates map: partNumber -> quantity to cancel
    const cancelUpdates = new Map<string, number>();

    splitData.forEach((split) => {
      const cancelSelection = cancelSelectionMap.get(
        split.supplierId ?? "remainingItems",
      );
      if (!cancelSelection) return;

      split.parts.forEach((part) => {
        const key = part.partNumber;
        const currentCancel = cancelUpdates.get(key) || 0;
        cancelUpdates.set(key, currentCancel + part.quantity);
      });
    });

    // Update order items
    return orderItems.map((item) => {
      const itemPartNumber = item.partNumber;
      const cancelQty = cancelUpdates.get(itemPartNumber) || 0;

      if (cancelQty === 0) return item;

      const currentCancel = item.cancelledQuantity || 0;
      const currentRemaining = item.remainingDecisionQuantity || 0;

      // Validate: can't cancel more than remaining
      const finalCancelQty = Math.min(cancelQty, currentRemaining);

      return {
        ...item,
        cancelledQuantity: currentCancel + finalCancelQty,
        remainingDecisionQuantity: currentRemaining - finalCancelQty,
      };
    });
  }

  /**
   * Pure function: Applies confirm quantities to order items.
   * Reduces remainingDecisionQuantity for each confirmed split (including remaining items where supplierId is null).
   * Does NOT modify orderedQuantity, holdQuantity, or cancelledQuantity.
   * Returns items unchanged if confirmSelections is empty/undefined.
   *
   * @param orderItems - Current order items (will be cloned, not mutated)
   * @param confirmSelections - Confirm selections from delivery options modal
   * @param splitData - Split data with parts mapping
   * @returns New array of order items with confirm applied
   */
  static applyConfirmToItems(
    orderItems: OrderItem[],
    confirmSelections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): OrderItem[] {
    if (!confirmSelections || confirmSelections.length === 0) {
      return orderItems;
    }

    // Map selections to parts by supplierId (null is valid for remaining items)
    const confirmSelectionMap = new Map(
      confirmSelections.map((s) => [s.supplierId ?? "remainingItems", s]),
    );

    // Build confirm quantity updates map: partNumber -> quantity confirmed
    const confirmUpdates = new Map<string, number>();

    splitData.forEach((split) => {
      const confirmSelection = confirmSelectionMap.get(
        split.supplierId ?? "remainingItems",
      );
      if (!confirmSelection) return;

      split.parts.forEach((part) => {
        const key = part.partNumber;
        const currentConfirm = confirmUpdates.get(key) || 0;
        confirmUpdates.set(key, currentConfirm + part.quantity);
      });
    });

    // Update order items - reduce remainingDecisionQuantity for confirmed quantities
    return orderItems.map((item) => {
      const itemPartNumber = item.partNumber;
      const confirmQty = confirmUpdates.get(itemPartNumber) || 0;

      if (confirmQty === 0) return item;

      const currentRemaining = item.remainingDecisionQuantity || 0;

      // Validate: can't confirm more than remaining
      const finalConfirmQty = Math.min(confirmQty, currentRemaining);

      return {
        ...item,
        remainingDecisionQuantity: currentRemaining - finalConfirmQty,
      };
    });
  }

  /**
   * Pure function: Computes updated orderETA with buy time entry.
   * Finds maximum buy time across all selections and creates new ETA entry.
   * Returns unchanged orderEta if buyTimeSelections is empty/undefined or has no valid buy time values.
   *
   * @param orderEta - Current orderETA array
   * @param buyTimeSelections - Buy time selections from delivery options modal
   * @param cptGivenTime - Timestamp when modal was confirmed (ISO string)
   * @returns Object with updatedEta array and isEtaUpdated boolean
   */
  static computeBuyTimeEta(
    orderEta: Array<{ etaGivenTime: string; eta: string; reason?: string }>,
    buyTimeSelections: NormalizedDeliverySelection[],
    cptGivenTime: string,
  ): {
    updatedEta: Array<{ etaGivenTime: string; eta: string; reason?: string }>;
    isEtaUpdated: boolean;
  } {
    if (!buyTimeSelections || buyTimeSelections.length === 0) {
      return { updatedEta: orderEta, isEtaUpdated: false };
    }

    // Filter to only selections with valid buyTimeValue
    const validBuyTimeSelections = buyTimeSelections.filter(
      (s) => s.buyTimeValue,
    );

    if (validBuyTimeSelections.length === 0) {
      return { updatedEta: orderEta, isEtaUpdated: false };
    }

    // Extract buy time values and find maximum (latest datetime)
    const buyTimeValues = validBuyTimeSelections
      .map((s) => new Date(s.buyTimeValue!).getTime())
      .filter((time) => !isNaN(time));

    if (buyTimeValues.length === 0) {
      return { updatedEta: orderEta, isEtaUpdated: false };
    }

    // Find maximum buy time (latest datetime)
    const maxBuyTimeISO = new Date(Math.max(...buyTimeValues)).toISOString();

    // Convert ISO strings to IST format (YYYY-MM-DDTHH:mm:ss) for orderETA
    const maxBuyTimeIST = dayjs(maxBuyTimeISO)
      .utc()
      .tz(IST_TIMEZONE)
      .format("YYYY-MM-DDTHH:mm:ss");
    const cptGivenTimeIST = dayjs(cptGivenTime)
      .utc()
      .tz(IST_TIMEZONE)
      .format("YYYY-MM-DDTHH:mm:ss");

    // Add new ETA entry to existing orderETA array
    const newEta = {
      etaGivenTime: cptGivenTimeIST,
      eta: maxBuyTimeIST,
      reason: "System: Buy Time",
    };

    const updatedEta = [...(orderEta || []), newEta];

    return { updatedEta, isEtaUpdated: true };
  }

  }
